// src/components/Dashboard/PlayerDashboard/PlayerStatsWidget.jsx
// Shows player's personal stats and metrics

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { 
  FaChartBar, FaCalendarCheck, FaTrophy, FaUsers, FaArrowRight 
} from 'react-icons/fa';
import styles from './PlayerStatsWidget.module.css';

const PlayerStatsWidget = ({ programs, events, userProfile, refreshTrigger }) => {
  const navigate = useNavigate();
  const auth = getAuth();

  // Calculate stats
  const stats = useMemo(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Events attended (approximation based on past events with RSVP)
    const pastEvents = (events || []).filter(e => {
      const eventDate = new Date(e.dateTime?.start);
      return eventDate < now && eventDate > thirtyDaysAgo;
    });

    const attendedEvents = pastEvents.filter(e => 
      e.userRsvp === 'going' || e.userRsvp === 'attended'
    ).length;

    // Calculate attendance rate
    const attendanceRate = pastEvents.length > 0 
      ? Math.round((attendedEvents / pastEvents.length) * 100) 
      : 100;

    // Total programs
    const programCount = programs?.length || 0;

    return {
      eventsAttended: attendedEvents,
      totalPastEvents: pastEvents.length,
      attendanceRate,
      programCount,
      upcomingEvents: (events || []).filter(e => new Date(e.dateTime?.start) > now).length
    };
  }, [events, programs]);

  const handleViewProfile = () => {
    const userId = auth.currentUser?.uid;
    if (userId) {
      navigate(`/player-profile/${userId}`);
    }
  };

  const getAttendanceColor = (rate) => {
    if (rate >= 80) return 'green';
    if (rate >= 60) return 'yellow';
    return 'red';
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <FaChartBar className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>My Stats</h3>
        </div>
        <span className={styles.period}>Last 30 days</span>
      </div>

      <div className={styles.content}>
        <div className={styles.statsGrid}>
          {/* Attendance Rate */}
          <div className={styles.statCard}>
            <div className={`${styles.statIcon} ${styles[getAttendanceColor(stats.attendanceRate)]}`}>
              <FaCalendarCheck />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{stats.attendanceRate}%</span>
              <span className={styles.statLabel}>Attendance</span>
            </div>
          </div>

          {/* Events Attended */}
          <div className={styles.statCard}>
            <div className={`${styles.statIcon} ${styles.blue}`}>
              <FaCalendarCheck />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{stats.eventsAttended}</span>
              <span className={styles.statLabel}>Events Attended</span>
            </div>
          </div>

          {/* Programs */}
          <div className={styles.statCard}>
            <div className={`${styles.statIcon} ${styles.purple}`}>
              <FaUsers />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{stats.programCount}</span>
              <span className={styles.statLabel}>Programs</span>
            </div>
          </div>

          {/* Upcoming */}
          <div className={styles.statCard}>
            <div className={`${styles.statIcon} ${styles.lime}`}>
              <FaTrophy />
            </div>
            <div className={styles.statInfo}>
              <span className={styles.statValue}>{stats.upcomingEvents}</span>
              <span className={styles.statLabel}>Upcoming</span>
            </div>
          </div>
        </div>

        <button className={styles.viewProfileBtn} onClick={handleViewProfile}>
          View Full Profile <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

export default PlayerStatsWidget;

